if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.confirmedVariant = this.getSelectedVariant(this);
        this.purchaseAvailable = !!this.confirmedVariant?.available && !this.querySelector('product-form:not(.sticky-atc-form) button[name="add"]')?.disabled;
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        this.syncPurchaseState();
        this.scheduleMediaWarmup();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.onVariantChangeUnsubscriber?.();
        this.onVariantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        if (this.swapUtilityInitialized) return;
        this.swapUtilityInitialized = true;
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const selection = this.querySelector('[data-mc-product-switcher]')?._mcSwitcher?.resolveSelection(target);
        const variantData = selection && this.getVariantData(selection.target.id);
        if (variantData) {
          this.applyVariantInstantly(selection, variantData, target.id);
          return;
        }
        if (selection) {
          this.resetProductFormState();
          const url = new URL(this.dataset.url, location.origin);
          url.searchParams.set('variant', selection.target.id);
          url.searchParams.set('section_id', this.sectionId);
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview || window.Shopify?.theme?.role === 'unpublished') url.searchParams.set('preview_theme_id', preview || window.Shopify.theme.id);
          this.renderProductInfo({
            requestUrl: url.href,
            targetId: target.id,
            callback: html => {
              this.handleUpdateProductInfo(this.dataset.url)(html);
              selection.complete();
            },
          });
          return;
        }
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      // R2-12: size/frame changes are set from the picker's variant JSON in the click frame.
      // The section request only follows as a background check for Liquid-only parts
      // (picker disabled states, best-for text, thumbnails, infographic, specs, features).
      applyVariantInstantly(selection, data, targetId) {
        this.abortController?.abort();
        const token = (this.variantToken || 0) + 1;
        this.variantToken = token;
        const variant = selection.target;

        // Heavy work after the next paint. Scheduled first, so the server check
        // still runs if one of the instant steps below fails.
        requestAnimationFrame(() => setTimeout(() => {
          if (token !== this.variantToken || !this.isConnected) return;
          // mc-room-guide syncs itself on the bubbling picker change, after this handler.
          this.querySelector('[data-mc-product-switcher]')?._mcSwitcher?.refresh();
          this.reconcileVariant(variant.id, targetId, token);
        }, 0));

        this.syncPickerSelection(variant.options);
        const selected = this.variantSelectors?.querySelector('[data-selected-variant]');
        if (selected) selected.textContent = JSON.stringify(variant);
        this.productForm?.handleErrorMessage?.();
        this.querySelector('mc-purchase-prices')?.apply?.(data);
        this.updateURL(this.dataset.url, variant.id);
        this.confirmPurchase(variant, data.available);
        if (data.featured_media) this.showVariantMedia(data.featured_media);
        selection.complete();
        // A new size has other frame images; warm those in idle time.
        this.scheduleMediaWarmup();
      }

      getVariantData(variantId) {
        if (!this.loadVariantData()) return null;
        const data = this.variantDataList.find((item) => String(item.id) === String(variantId));
        return data && typeof data.money === 'string' && 'compare_money' in data ? data : null;
      }

      loadVariantData() {
        const node = this.variantSelectors?.querySelector('[data-mc-size-variants]');
        if (!node) return false;
        if (this.variantDataNode !== node) {
          try {
            const parsed = JSON.parse(node.textContent);
            this.variantDataList = parsed.variants || [];
            this.variantDataOptions = parsed.options || [];
          } catch {
            return false;
          }
          this.variantDataNode = node;
        }
        return true;
      }

      // R2-14: line item for the instant ATC popup, built only from data on the page.
      // The cart/add response replaces it; null means no instant popup.
      getAddPreviewItem(variantId, quantity = 1) {
        const selected = this.getSelectedVariant(this);
        const variant = selected && String(selected.id) === String(variantId) ? selected : null;
        const data = this.loadVariantData() ? this.variantDataList.find((item) => String(item.id) === String(variantId)) : null;
        if (!variant && !data) return null;
        const heading = this.querySelector('.product__title h1');
        const title = heading?.dataset.mcOriginal || heading?.getAttribute('aria-label') || heading?.textContent.trim();
        const price = Number(data?.price ?? variant?.price);
        if (!title || !Number.isFinite(price)) return null;
        const compare = Number(data?.compare_at_price ?? variant?.compare_at_price) || 0;
        const values = variant?.options || data?.options || [];
        const mediaId = variant?.featured_media?.id ?? data?.featured_media;
        const gallery = this.querySelector('media-gallery');
        const slideImage =
          (mediaId &&
            gallery?.querySelector(
              `.product__media-list > [data-media-id="${this.dataset.section}-${mediaId}"] .product__media img`
            )) ||
          gallery?.querySelector('.product__media img');
        const featured = variant?.featured_image?.src
          ? { url: variant.featured_image.src, width: variant.featured_image.width }
          : slideImage
          ? { url: slideImage.currentSrc || slideImage.src, width: slideImage.naturalWidth || undefined }
          : null;
        return {
          id: Number(variantId),
          variant_id: Number(variantId),
          product_title: title,
          url: `${this.dataset.url}?variant=${variantId}`,
          quantity,
          final_line_price: price * quantity,
          compare_line: compare > price ? compare * quantity : 0,
          featured_image: featured,
          options_with_values: (this.variantDataOptions || [])
            .map((name, index) => ({ name, value: values[index] }))
            .filter((option) => option.name && option.value),
        };
      }

      // R2-13: after load, in idle time, fetch and decode the gallery images of the
      // frame variants of the selected size, with the gallery's own srcset/sizes.
      scheduleMediaWarmup() {
        if (this.warmupScheduled || !this.querySelector('media-gallery')) return;
        this.warmupScheduled = true;
        const idle = () => {
          const run = () => {
            this.warmupScheduled = false;
            if (this.isConnected) this.warmVariantMedia();
          };
          if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 3000 });
          else setTimeout(run, 1000);
        };
        if (document.readyState === 'complete') idle();
        else window.addEventListener('load', idle, { once: true });
      }

      warmVariantMedia() {
        const gallery = this.querySelector('media-gallery');
        const current = this.getSelectedVariant(this);
        if (!gallery || !current || !this.loadVariantData()) return;
        const sizeIndex = this.variantDataOptions.findIndex((name) => String(name).toLowerCase() === 'size');
        if (sizeIndex < 0) return;
        const size = current.options?.[sizeIndex];
        this.warmedMedia = this.warmedMedia || new Map();
        const mediaIds = new Set(
          this.variantDataList
            .filter((item) => item.options?.[sizeIndex] === size && item.featured_media)
            .map((item) => String(item.featured_media))
        );
        mediaIds.forEach((mediaId) => {
          const image = gallery.querySelector(
            `.product__media-list > [data-media-id="${this.dataset.section}-${mediaId}"] .product__media img`
          );
          if (!image?.srcset || (image.complete && image.naturalWidth)) return;
          const key = `${image.sizes}|${image.srcset}`;
          if (this.warmedMedia.get(mediaId) === key) return;
          this.warmedMedia.set(mediaId, key);
          const preload = new Image();
          preload.decoding = 'async';
          preload.sizes = image.sizes;
          preload.srcset = image.srcset;
          preload.src = image.src;
          preload.decode().catch(() => this.warmedMedia.delete(mediaId));
        });
      }

      // The switcher may resolve to another size or frame than the clicked one.
      syncPickerSelection(options) {
        const picker = this.variantSelectors;
        if (!picker) return;
        let changed = false;
        picker.querySelectorAll('[data-mc-option-position]').forEach((group) => {
          const value = options?.[Number(group.dataset.mcOptionPosition) - 1];
          if (value === undefined) return;
          const select = group.querySelector('select');
          if (select) {
            if (select.value === value) return;
            const option = [...select.options].find((item) => item.value === value);
            if (!option) return;
            select.querySelector('option[selected]')?.removeAttribute('selected');
            option.setAttribute('selected', 'selected');
            select.value = value;
            changed = true;
            return;
          }
          const input = [...group.querySelectorAll('input[type="radio"]')].find((item) => item.value === value);
          if (!input || input.checked) return;
          input.checked = true;
          const label = group.querySelector('[data-selected-value]');
          if (label) label.textContent = value;
          changed = true;
        });
        if (changed) window.mcSizePrices?.update(picker);
      }

      showVariantMedia(mediaId) {
        const gallery = this.querySelector('media-gallery');
        const slide = gallery?.querySelector(`.product__media-list > [data-media-id="${this.dataset.section}-${mediaId}"]`);
        const roomActive = !!gallery?.dataset.currentMedia?.endsWith('-room-guide');
        const token = (this.mediaToken || 0) + 1;
        this.mediaToken = token;
        const show = () => {
          // A later change (click or section response) owns the gallery now.
          if (token !== this.mediaToken || !this.isConnected) return;
          const selectedMedia = this.getSelectedVariant(this)?.featured_media?.id;
          if (selectedMedia && String(selectedMedia) !== String(mediaId)) return;
          // The section response renders the variant image first; take that order now,
          // so the background reorder does not move the visible slide again.
          if (slide?.isConnected && !gallery.dataset.currentMedia?.endsWith('-room-guide') && slide.parentElement.firstElementChild !== slide) {
            slide.parentElement.prepend(slide);
            gallery.elements?.viewer?.resetPages?.();
          }
          // Covers MediaGallery-<section>, as in the section-response path.
          this.applyCustomMediaLogic(mediaId);
        };
        // R2-13: keep the old image until the new one is decoded, at most ~150 ms.
        const image = slide?.querySelector('.product__media img');
        if (!image || roomActive || (image.complete && image.naturalWidth)) return show();
        if (image.loading === 'lazy') image.loading = 'eager';
        let timer;
        Promise.race([image.decode(), new Promise((resolve) => (timer = setTimeout(resolve, 150)))])
          .catch(() => {})
          .then(() => {
            clearTimeout(timer);
            show();
          });
      }

      reconcileVariant(variantId, targetId, token) {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', this.sectionId);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview || window.Shopify?.theme?.role === 'unpublished') url.searchParams.set('preview_theme_id', preview || window.Shopify.theme.id);
        this.renderProductInfo({
          requestUrl: url.href,
          callback: (html) => {
            // Only use the answer while it still matches the visible selection.
            if (token !== this.variantToken || String(this.getSelectedVariant(this)?.id) !== String(variantId)) return;
            const hadFocus = !!this.variantSelectors?.contains(document.activeElement);
            this.handleUpdateProductInfo(this.dataset.url, true)(html);
            if (hadFocus && targetId) this.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          },
          onError: (error) => {
            // A different variant in the answer means stale page data: block the purchase.
            // Network errors keep the state from the variant JSON; cart/add validates anyway.
            if (error.message === 'Unexpected variant response') this.failPurchaseChange();
            else console.warn('Variant check failed', error);
          },
        });
      }

      resetProductFormState() {
        this.beginPurchaseChange();
      }

      beginPurchaseChange() {
        this.abortController?.abort();
        this.purchasePending = true;
        this.purchaseFailed = false;
        this.productForm?.handleErrorMessage?.();
        this.syncPurchaseState();
      }

      failPurchaseChange() {
        this.purchasePending = false;
        this.purchaseFailed = true;
        this.syncPurchaseState();
        this.productForm?.handleErrorMessage?.(window.cartStrings.error);
      }

      confirmPurchase(variant, available = variant?.available) {
        this.confirmedVariant = variant;
        this.purchaseAvailable = !!available && !!variant?.available;
        this.purchasePending = false;
        this.purchaseFailed = false;
        this.updateVariantInputs(variant?.id);
        this.syncPurchaseState();
      }

      get purchaseBlocked() {
        return !!(this.purchasePending || this.purchaseFailed || !this.purchaseAvailable);
      }

      syncPurchaseState() {
        const form = this.productForm;
        if (!form?.toggleSubmitButton) return;
        form.variantIdInput.disabled = this.purchaseBlocked;
        const text = this.confirmedVariant ? window.variantStrings.soldOut : window.variantStrings.unavailable;
        form.toggleSubmitButton(!!(this.purchaseBlocked || form.submitting || form.error), this.purchasePending || this.purchaseFailed ? undefined : text);
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, onError }) {
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;

        fetch(requestUrl, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Product request failed');
            return response.text();
          })
          .then((responseText) => {
            if (controller !== this.abortController || controller.signal.aborted || !this.isConnected) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const expected = new URL(requestUrl, location.origin).searchParams.get('variant');
            const returned = this.getSelectedVariant(html);
            if (expected && returned && String(returned.id) !== expected) throw new Error('Unexpected variant response');
            callback(html);
          })
          .then(() => {
            if (controller !== this.abortController || controller.signal.aborted || !this.isConnected) return;
            // set focus to last clicked option value
            if (targetId) this.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (controller !== this.abortController || controller.signal.aborted || !this.isConnected) return;
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else if (onError) {
              onError(error);
            } else {
              this.failPurchaseChange();
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          const positions = [...this.variantSelectors.querySelectorAll('[data-mc-option-position]')]
            .map(row => ({ position: row.dataset.mcOptionPosition, left: row.scrollLeft }));
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks, [
            node => positions.forEach(({ position, left }) => {
              const row = node.querySelector(`[data-mc-option-position="${position}"]`);
              if (row) row.scrollLeft = left;
            }),
          ]);
        }
      }

      // reconcile: background check after applyVariantInstantly. URL, inputs and the active
      // slide are already set; only touch them when the answer differs.
      handleUpdateProductInfo(productUrl, reconcile = false) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          if (!reconcile) {
            this.updateURL(productUrl, variant?.id);
            this.updateVariantInputs(variant?.id);
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id, reconcile);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.querySelector('mc-purchase-prices')?.update(html);
          const available = html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.disabled === false;
          if (reconcile && this.purchaseAvailable === (available && !!variant.available) && !this.purchasePending && !this.purchaseFailed) {
            this.confirmedVariant = variant;
          } else {
            this.confirmPurchase(variant, available);
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.confirmPurchase(null, false);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId, reconcile = false) {
        this.updateThumbnailVisibility(html);
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
        const featuredIndex = () => [...(mediaGallerySource?.children || [])].findIndex(
          (item) => item.dataset.mediaId === `${this.dataset.section}-${variantFeaturedMediaId}`
        );
        const startIndex = featuredIndex();

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // The click frame already activated this media; repeat only if the reorder moved it.
        const activate = !reconcile || startIndex < 0 || featuredIndex() !== startIndex;

        // set featured media as active in the media gallery
        if (activate) {
          this.querySelector(`media-gallery`)?.setActiveMedia?.(
            `${this.dataset.section}-${variantFeaturedMediaId}`,
            true
          );
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

        this.applyCustomMediaLogic(variantFeaturedMediaId, activate);
      }

      updateThumbnailVisibility(html) {
        // P8b: consume the same Liquid decision as the initial render, in this section only.
        const source = html.querySelector('product-info') || html;
        const sources = new Map(Array.from(source.querySelectorAll('[data-thumbnail-variant]'), item => [item.dataset.target, item]));
        this.querySelectorAll('[data-thumbnail-variant]').forEach(item => {
          const sourceItem = sources.get(item.dataset.target);
          if (!sourceItem) return;
          const hidden = sourceItem.dataset.mcThumbnailHidden === 'true';
          // Plan 13 B2: hidden thumbnails render without <img>; take it from the response.
          if (!hidden && item.hasAttribute('data-mc-deferred')) {
            const button = item.querySelector('button');
            const sourceButton = sourceItem.querySelector('button');
            if (button && sourceButton?.querySelector('img')) {
              button.innerHTML = sourceButton.innerHTML;
              item.removeAttribute('data-mc-deferred');
            }
          }
          item.dataset.mcThumbnailHidden = String(hidden);
          item.style.display = hidden ? 'none' : 'block';
        });
      }

      applyCustomMediaLogic(variantFeaturedMediaId, activate = true) {
        if (activate) this.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`).forEach(gallery =>
          gallery.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true)
        );
        const modalContent = this.productModal?.querySelector('.product-media-modal__content');
        const activeMedia = modalContent?.querySelector(`[data-media-id="${variantFeaturedMediaId}"]`);
        if (activeMedia) modalContent.prepend(activeMedia);
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector('product-form:not(.sticky-atc-form)');
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}